import { useState, useRef, useEffect } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faBell,
  faBars,
  faMagnifyingGlass,
  faStore,
  faUser,
  faKey,
  faRightFromBracket,
} from "@fortawesome/free-solid-svg-icons";
import { useLocation, useNavigate } from "react-router-dom";
import { Button } from "../../../components/ui/button";
import { Input } from "../../../components/ui/input";
import { getPageTitle } from "./navLinks";
import { Avatar, AvatarFallback } from "../../../components/ui/avatar";
import { useAuth } from "../../../hooks";
import { toastSuccess, toastError } from "../../../utils/toast";

const TopHeader = ({ onMenuToggle }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, userAccount, logout } = useAuth();
  const pageTitle = getPageTitle(location.pathname);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isLoggingOut, setIsLoggingOut] = useState(false);
  const dropdownRef = useRef(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const getUserInitials = () => {
    if (!user) return "U";
    const name = userAccount?.full_name || user?.full_name || "User";
    const parts = name.split(" ");
    if (parts.length >= 2) {
      return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
    }
    return name.substring(0, 2).toUpperCase();
  };

  const getUserName = () => {
    if (!user) return "User";
    return userAccount?.full_name || user?.full_name || "User";
  };

  // Get user role display
  const getUserRole = () => {
    if (!user) return "";
    const roleMap = {
      super_admin: "Super Admin",
      admin: "Administrator",
      owner: "Shop Owner",
      seller: "Shop Seller",
    };
    return roleMap[user.role] || user.role;
  };

  // Handle logout
  const handleLogout = async () => {
    try {
      setIsLoggingOut(true);
      await logout();
      toastSuccess("Logged out successfully");
      navigate("/login");
    } catch (error) {
      toastError("Failed to logout. Please try again.");
      console.error("Logout error:", error);
    } finally {
      setIsLoggingOut(false);
      setIsDropdownOpen(false);
    }
  };

  const menuItems = [
    {
      label: "My profile",
      icon: faUser,
      href: "/app/profile",
      onClick: () => navigate("/app/profile"),
    },
    {
      label: "Change password",
      icon: faKey,
      href: "/app/changePassword",
      onClick: () => navigate("/app/changePassword"),
    },
    { divider: true },
    {
      label: isLoggingOut ? "Logging out..." : "Logout",
      icon: faRightFromBracket,
      href: "/login",
      onClick: handleLogout,
      isDanger: true,
      disabled: isLoggingOut,
    },
  ];

  return (
    <header className="sticky top-0 z-30 flex h-16 items-center justify-between border-b border-gray-200 bg-white px-4 lg:px-6">
      {/* LEFT */}
      <div className="flex items-center gap-3">
        {/* Mobile menu button */}
        <Button
          variant="ghost"
          size="icon"
          className="lg:hidden"
          onClick={onMenuToggle}
        >
          <FontAwesomeIcon icon={faBars} className="h-5 w-5 text-gray-700" />
        </Button>

        {/* Store */}
        <div className="flex items-center gap-2">
          <FontAwesomeIcon icon={faStore} className="h-5 w-5 text-blue-600" />
          <span className="hidden sm:inline text-lg font-bold text-gray-800">
            Shop Kiganjani
          </span>
        </div>

        <span className="hidden md:inline text-gray-600">/</span>

        <h1 className="hidden md:inline text-sm font-medium text-gray-600">
          {pageTitle}
        </h1>
      </div>

      {/* RIGHT */}
      <div className="flex items-center gap-2">
        <div className="relative hidden md:block">
          <FontAwesomeIcon
            icon={faMagnifyingGlass}
            className="absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-600"
          />
          <Input placeholder="Search..." className="w-64 pl-8" />
        </div>

        {/* Notification Bell */}
        <div className="relative">
          <Button
            variant="ghost"
            size="icon"
            className="relative hover:bg-gray-100 rounded-full transition-colors"
          >
            <FontAwesomeIcon icon={faBell} className="h-5 w-5 text-gray-700" />
            <span className="absolute right-1.5 top-1.5 h-2 w-2 rounded-full bg-red-500 animate-pulse" />
          </Button>
        </div>

        {/* User Menu Dropdown */}
        <div className="relative" ref={dropdownRef}>
          <Button
            variant="ghost"
            size="icon"
            className="relative hover:bg-gray-100 rounded-full transition-colors"
            onClick={() => setIsDropdownOpen(!isDropdownOpen)}
          >
            <Avatar className="h-8 w-8">
              <AvatarFallback className="bg-blue-100 text-blue-700 text-sm font-semibold">
                {getUserInitials()}
              </AvatarFallback>
            </Avatar>
          </Button>

          {/* Dropdown Menu */}
          {isDropdownOpen && (
            <div className="absolute right-0 mt-2 w-56 bg-white rounded-lg shadow-xl border border-gray-200 overflow-hidden z-50 animate-in slide-in-from-top-2 duration-200">
              {/* Header */}
              <div className="px-4 py-3 bg-gradient-to-r from-blue-50 to-white border-b border-gray-100">
                <div className="flex items-center gap-3">
                  <Avatar className="h-9 w-9">
                    <AvatarFallback className="bg-blue-100 text-blue-700 text-sm font-semibold">
                      {getUserInitials()}
                    </AvatarFallback>
                  </Avatar>
                  <div>
                    <p className="text-sm font-semibold text-gray-900">
                      {getUserName()}
                    </p>
                    <p className="text-xs text-gray-600">{getUserRole()}</p>
                  </div>
                </div>
              </div>

              {/* Menu Items */}
              <div className="py-1">
                {menuItems.map((item, index) => {
                  if (item.divider) {
                    return (
                      <div
                        key={index}
                        className="border-t border-gray-100 my-1"
                      />
                    );
                  }
                  return (
                    <button
                      key={index}
                      onClick={() => {
                        if (!item.disabled) {
                          setIsDropdownOpen(false);
                          item.onClick();
                        }
                      }}
                      disabled={item.disabled}
                      className={`w-full flex items-center gap-3 px-4 py-2.5 text-sm transition-colors ${
                        item.disabled
                          ? "opacity-50 cursor-not-allowed"
                          : item.isDanger
                            ? "text-red-600 hover:bg-red-50 hover:text-red-700"
                            : "text-gray-700 hover:bg-blue-50 hover:text-blue-700"
                      }`}
                    >
                      <FontAwesomeIcon
                        icon={item.icon}
                        className={`h-4 w-4 ${
                          item.isDanger ? "text-red-500" : "text-gray-600"
                        }`}
                      />
                      <span>{item.label}</span>
                      {item.isDanger && isLoggingOut && (
                        <span className="ml-auto h-4 w-4 border-2 border-red-600 border-t-transparent rounded-full animate-spin" />
                      )}
                    </button>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      </div>
    </header>
  );
};

export default TopHeader;
